import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, IconButton, ListSubheader, MenuItem, Paper, Select, SelectChangeEvent, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React from 'react';
import { DashboardFilterType, useDashboardFilters } from '../../../hooks/useDashboardFilters';
import { useDashboardPagination } from '../../../hooks/useDashboardPagination';
import { useUserDashboardSettings } from '../../../hooks/useUserDashboardSettings';
import { TaskFilterParam, useGetAllTasks } from '../../../services/react-query/tasks';
import { useGetCurrentUserSettings } from '../../../services/react-query/users/useGetCurrentUserSettings';
import { Filter } from '../../../types/Filter';
import { TaskStatusFilter } from '../../../types/Task';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import {
  controlButtonStyles,
  controlSectionStyles,
  dashboardSelectSx,
  iconButtonStyles,
  paperContainerStyles,
  tilesContainerStyles,
} from '../dashboardStyles';
import { TaskFooter } from './TaskFooter';
import { TaskTile } from './TaskTile';

interface ITasksPanelProps {
  currentEmployeeUuid?: string;
}

export function TasksPanel({ currentEmployeeUuid }: ITasksPanelProps) {
  const { dashboardSettings, updateSettings } = useUserDashboardSettings(UserConfigurableModule.TASKS);

  const { data: currentUserSettings, isLoading: isUserSettingsLoading } = useGetCurrentUserSettings();

  // Apply employee restriction when "My Tasks" mode is active
  const employees = currentEmployeeUuid && dashboardSettings.ownership === 'my' ? [currentEmployeeUuid] : [];

  // Built-in status filters for tasks
  const defaultFilters: Filter[] = [
    {
      uuid: TaskStatusFilter.ACTIVE,
      name: 'Active',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.ACTIVE)] },
    },
    {
      uuid: TaskStatusFilter.NOT_STARTED,
      name: 'Not Started',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.NOT_STARTED)] },
    },
    {
      uuid: TaskStatusFilter.READY,
      name: 'Ready',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.READY)] },
    },
    {
      uuid: TaskStatusFilter.IN_PROGRESS,
      name: 'In Progress',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.IN_PROGRESS)] },
    },
    {
      uuid: TaskStatusFilter.ON_HOLD,
      name: 'On Hold',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.ON_HOLD)] },
    },
    {
      uuid: TaskStatusFilter.OVERDUE,
      name: 'Overdue',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.OVERDUE)] },
    },
    {
      uuid: TaskStatusFilter.COMPLETED,
      name: 'Completed',
      page: UserConfigurableModule.TASKS,
      content: { employee: employees, task_status: [String(TaskStatusFilter.COMPLETED)] },
    },
  ];

  // Default filter UUIDs to distinguish default vs saved filters
  const defaultUuids = Object.values(TaskStatusFilter);

  // Centralized filter logic + restore from saved span
  const { savedFilters, selectedFilter, setSelectedFilter, filterType, setFilterType, filtersParam, isUserFiltersLoading } = useDashboardFilters({
    module: UserConfigurableModule.TASKS,
    defaultFilters,
    defaultUuids,
    savedSpan: dashboardSettings.filters?.span,
  });

  // Extracted pagination logic
  const { page, setNewPage } = useDashboardPagination();

  // Fetch tasks using resolved filter settings
  const { data, isLoading: isTasksLoading } = useGetAllTasks({
    enabled: !!selectedFilter,
    params: {
      page,
      per_page: 6,
      filters: filtersParam as TaskFilterParam,
    },
  });

  if (isTasksLoading || isUserFiltersLoading || isUserSettingsLoading || !selectedFilter) {
    return <Loading />;
  }

  // Total pages from API response
  const maxPage = data?.meta?.total_pages || 1;

  // When user selects a filter from dropdown
  const handleFilterChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setSelectedFilter(value); // update UI

    // Detect whether it's a default or saved filter
    const isDefault = defaultUuids.includes(value as TaskStatusFilter);
    setFilterType(isDefault ? DashboardFilterType.DEFAULT : DashboardFilterType.SAVED);

    // Persist user's chosen filter span
    updateSettings({ filters: { span: value } });
  };

  return (
    <Box component={Paper} sx={paperContainerStyles}>
      <Box sx={controlSectionStyles}>
        {/* Admin-only ownership toggle */}
        {currentUserSettings?.settings.isAdmin ? (
          <ToggleButtonGroup
            exclusive
            value={dashboardSettings.ownership}
            onChange={(_e, v) => v && updateSettings({ ownership: v })}
            size="small"
            disabled={filterType === DashboardFilterType.SAVED} // saved filters lock ownership
          >
            <ToggleButton value="my" disabled={!currentEmployeeUuid} sx={controlButtonStyles}>
              My Tasks
            </ToggleButton>
            <ToggleButton value="all" sx={controlButtonStyles}>
              All Tasks
            </ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Typography variant="h6" sx={{ height: '3rem', fontWeight: 600, pt: 1 }}>
            Tasks
          </Typography>
        )}

        {/* Filter dropdown */}
        <Select size="small" sx={dashboardSelectSx} value={selectedFilter} onChange={handleFilterChange}>
          <ListSubheader>Default</ListSubheader>
          {defaultFilters.map((f) => (
            <MenuItem key={f.uuid} value={f.uuid}>
              {f.name}
            </MenuItem>
          ))}

          {savedFilters.length > 0 && [
            <ListSubheader key="saved-header">Saved Filters</ListSubheader>,
            ...savedFilters.map((f) => (
              <MenuItem key={f.uuid} value={f.uuid}>
                {f.name}
              </MenuItem>
            )),
          ]}
        </Select>

        {/* Pagination controls */}
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page - 1, maxPage)} disabled={page <= 1}>
            <KeyboardArrowLeft />
          </IconButton>

          <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page + 1, maxPage)} disabled={page >= maxPage}>
            <KeyboardArrowRight />
          </IconButton>
        </Box>
      </Box>

      {/* Task Cards */}
      <Box sx={tilesContainerStyles}>
        {data?.data.map((task) => <TaskTile key={task.uuid} task={task} currentEmployeeUuid={currentEmployeeUuid} />)}
      </Box>

      {/* Footer summary */}
      <TaskFooter filters={filtersParam} />
    </Box>
  );
}
